import { ReactElement, useEffect, useState } from "react";
import styled from "styled-components";

import SectionContainer from "../../container/SectionContainer";
import { ColorV2, Form, Text } from "@adoptaunabuelo/react-components";
import { Product, Track, User, UserScheme } from "client-v2";
import { actionsCreators, State } from "../../../redux";
import { useSelector } from "react-redux";
import { useGoogleMaps } from "../../../layout/GoogleMapsContext";
import { bindActionCreators } from "@reduxjs/toolkit";
import { useDispatch } from "react-redux";
import AddressConfirmationModal from "./AddressConfirmationModal";
import { AlertCircle } from "lucide-react";

const Container = styled.div`
	display: flex;
	flex-direction: column;
	flex: 1;
	width: 100%;
`;
const Error = styled.div`
	display: flex;
	background-color: ${ColorV2.surface.neutralSoft};
	padding: 12px 16px;
	border-radius: 16px;
	gap: 8px;
`;

const ShippingLocationStep = (props: ShippingLocationProps) => {
	const { isLoaded } = useGoogleMaps();
	const dispatch = useDispatch();
	const { setShowError } = bindActionCreators(actionsCreators, dispatch);
	const translation = useSelector(
		(state: State) => state.location.translation
	);
	const currentUser = useSelector((state: State) => state.user.currentUser);
	const isMobile = useSelector((state: State) => state.screen.isMobile);
	const colors = {
		buttonBackground: ColorV2.surface.primary,
		buttonColor: "white",
		animation: ["#00315C", "#005AA8"],
	};

	const [inputError, setInputError] = useState<string | undefined>(undefined);
	const [loading, setLoading] = useState(false);
	const [location, setLocation] = useState<LocationObjProps | undefined>(
		undefined
	);
	const [shipping, setShipping] = useState<
		{ amount: number; currency: string } | undefined
	>(undefined);
	const [showConfirmationModal, setShowConfirmationModal] = useState(false);
	const [showShippingError, setShowShippingError] = useState(false);

	useEffect(() => {
		//Track on Mixpanel
		Track.event({
			event: "shop product location view init",
			user: currentUser,
			params: {
				view: props.view,
			},
		});
	}, []);

	const onConfirmationClick = () => {
		if (
			location &&
			location.route &&
			location.zipCode &&
			location.country
		) {
			//Track on Mixpanel
			Track.event({
				event: "shop product location added",
				user: currentUser,
				params: {
					view: props.view,
				},
			});
			props.onFinish({
				data: {
					route: location.route,
					routeNumber: location.routeNumber,
					routeInfo: location.routeInfo,
					city: location.city,
					zipCode: location.zipCode,
					country: location.country,
				},
			});
		} else {
			setInputError(
				"Debes añadir una dirección válida antes de continuar."
			);
		}
	};

	const onLocationChange = async (item: {
		data?: LocationObjProps;
		error?: string;
	}) => {
		if (item.data?.location) {
			setInputError(undefined);
			setLocation(item.data);
			setShowShippingError(false);

			//Update shipping price if the location change
			if (
				item.data.shortCountry &&
				item.data.shortCountry !== location?.shortCountry
			) {
				setLoading(true);
				Product.getShipping({
					countryCode: item.data.shortCountry,
					currency: "EUR",
				})
					.then((result) => {
						setShipping(result);
						props.onShippingChange(result);
						setLoading(false);
					})
					.catch((e) => {
						setLoading(false);
						if (e.code === 604) setShowShippingError(true);
						else setShowError({ show: true, message: e.message });
					});
			}
		} else {
			setInputError(
				"Debes indicar una dirección más precisa para que podamos hacer el envío."
			);
		}
	};

	return (
		<SectionContainer
			title={props.title ? props.title : "Dirección de envío"}
			subtitle={
				props.subtitle
					? props.subtitle
					: "Dinos la dirección completa donde quieres que enviemos el regalo."
			}
			childrenProps={{ style: { flex: 1, marginBottom: isMobile && 8 } }}
			buttonProps={{
				style: {
					backgroundColor: colors.buttonBackground,
					color: colors.buttonColor,
				},
				backgroundColor: isMobile && ColorV2.surface.background,
				LeftView: props.Footer,
				loading: loading,
				children: translation.component_button_next,
				disabled: showShippingError,
				onClick: () => {
					if (
						location &&
						location.route &&
						location.routeNumber &&
						location.zipCode &&
						location.country
					) {
						if (shipping) {
							setShowConfirmationModal(true);
						} else {
							setInputError(
								"Debes indicar una dirección más precisa para que podamos hacer el envío."
							);
						}
					} else {
						setInputError(
							"Debes añadir una dirección válida antes de continuar."
						);
					}
				},
			}}
		>
			{location &&
				location &&
				location.route &&
				location.routeNumber &&
				location.zipCode &&
				location.country && (
					<AddressConfirmationModal
						isVisible={showConfirmationModal}
						location={{
							route: location.route,
							routeNumber: location.routeNumber,
							routeInfo: location.routeInfo,
							city: location.city,
							zipCode: location.zipCode,
							country: location.country,
						}}
						onClose={() => setShowConfirmationModal(false)}
						onConfirm={() => {
							setShowConfirmationModal(false);
							onConfirmationClick();
						}}
					/>
				)}
			<Container>
				<Form
					type="location"
					isLoaded={isLoaded}
					design="secondary"
					style={{ width: "100%" }}
					onSubmit={onLocationChange}
				/>
				{inputError && (
					<Text
						type="p"
						weight="medium"
						style={{ color: ColorV2.text.red, marginTop: 8 }}
					>
						{inputError}
					</Text>
				)}
			</Container>
			{shipping?.amount ? (
				<Error>
					<AlertCircle
						height={16}
						width={16}
						color={ColorV2.text.neutralMedium}
					/>
					<Text
						type="c1"
						style={{ color: ColorV2.text.neutralMedium, flex: 1 }}
					>
						El envío tiene un coste adicional de{" "}
						{new Intl.NumberFormat("es-ES", {
							style: "currency",
							currency: "EUR",
							maximumFractionDigits: 0,
						}).format(shipping.amount)}{" "}
						según la dirección indicada. ¡Gracias por hacer llegar
						tu Pack Solidario tan lejos!
					</Text>
				</Error>
			) : showShippingError ? (
				<Error>
					<AlertCircle
						height={16}
						width={16}
						color={ColorV2.text.neutralMedium}
					/>
					<Text
						type="c1"
						style={{ color: ColorV2.text.neutralMedium, flex: 1 }}
					>
						Por ahora solo podemos enviar Packs dentro de España.
						Pero puedes participar escribiendo tu carta online{" "}
						<a href="https://adoptaunabuelo.org/una-carta-para-un-abuelo/">
							aquí
						</a>
						.
					</Text>
				</Error>
			) : null}
		</SectionContainer>
	);
};
export default ShippingLocationStep;
export interface ShippingLocationProps {
	title?: string;
	subtitle?: string;
	user: UserScheme;
	Footer?: ReactElement;
	price: number;
	shipping: number;
	view: string;
	onShippingChange: (result: { amount: number; currency: string }) => void;
	onFinish: (result: {
		data?: {
			route: string;
			routeNumber?: string;
			routeInfo?: string;
			city?: string;
			zipCode: string;
			country: string;
		};
		error?: string;
	}) => void;
}

interface LocationObjProps {
	address?: string;
	sortAddress?: string;
	route?: string;
	routeNumber?: string;
	routeInfo?: string;
	city?: string;
	province?: string;
	zipCode?: string;
	country?: string;
	shortCountry?: string;
	location?: google.maps.LatLngLiteral;
	timeZone?: string;
}
